export type TooltipPlacement =
  | 'bottom'
  | 'top'
  | 'left'
  | 'right'
  | 'bottomLeft'
  | 'bottomRight'
  | 'topLeft'
  | 'topRight';

export type VerticalTooltipPlacement = 'bottom' | 'top';

type MeasureTextHeightParams = {
  text: string;
  width: number;
  sourceElement?: HTMLElement | null;
};

// Measure full (unclamped) text height off-screen.
// We can't rely on on-card text because it is line-clamped.
export const measureTextHeight = ({ text, width, sourceElement }: MeasureTextHeightParams) => {
  if (!text || typeof window === 'undefined') return 0;

  const measure = document.createElement('div');
  measure.style.position = 'absolute';
  measure.style.visibility = 'hidden';
  measure.style.pointerEvents = 'none';
  measure.style.zIndex = '-1';
  measure.style.width = `${width}px`;
  measure.style.whiteSpace = 'normal';
  measure.style.wordBreak = 'break-word';

  if (sourceElement) {
    // Copy font-related styles so height matches the real tooltip text.
    const styles = window.getComputedStyle(sourceElement);
    measure.style.fontSize = styles.fontSize;
    measure.style.fontFamily = styles.fontFamily;
    measure.style.fontWeight = styles.fontWeight;
    measure.style.lineHeight = styles.lineHeight;
    measure.style.letterSpacing = styles.letterSpacing;
  }

  measure.textContent = text;
  document.body.appendChild(measure);
  const height = measure.getBoundingClientRect().height;
  document.body.removeChild(measure);

  return height;
};

type TooltipPlacementParams = {
  containerRect: DOMRect;
  anchorRect: DOMRect;
  viewportWidth: number;
  viewportHeight: number;
  tooltipWidth: number;
  tooltipHeight: number;
};

export const getPlaylistCardTooltipPlacements = ({
  containerRect,
  anchorRect,
  viewportWidth,
  viewportHeight,
  tooltipWidth,
  tooltipHeight,
}: TooltipPlacementParams) => {
  const cardSpaceAbove = containerRect.top;
  const cardSpaceBelow = viewportHeight - containerRect.bottom;
  // Card-level tooltip (Open playlist) prefers the side with more space.
  const cardTooltipPlacement: VerticalTooltipPlacement =
    cardSpaceBelow > cardSpaceAbove ? 'bottom' : 'top';

  const spaceRight = viewportWidth - anchorRect.right;
  const spaceLeft = anchorRect.left;
  const spaceAbove = anchorRect.top;
  const spaceBelow = viewportHeight - anchorRect.bottom;
  const canFitBelow = spaceBelow >= tooltipHeight;
  const canFitAbove = spaceAbove >= tooltipHeight;
  const alignRight = spaceRight < tooltipWidth && spaceLeft >= tooltipWidth;

  let descriptionTooltipPlacement: TooltipPlacement;

  // If the tooltip can't fit vertically, try horizontal placement first.
  if (!canFitBelow && !canFitAbove) {
    if (spaceRight >= tooltipWidth) {
      descriptionTooltipPlacement = 'right';
    } else if (spaceLeft >= tooltipWidth) {
      descriptionTooltipPlacement = 'left';
    } else {
      // Last resort: pick the side with more vertical space.
      descriptionTooltipPlacement = spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }
    return { cardTooltipPlacement, descriptionTooltipPlacement };
  }

  // Prefer bottom if it fits; otherwise, use top. Align to avoid clipping by the sidebar.
  if (canFitBelow) {
    descriptionTooltipPlacement = alignRight ? 'bottomRight' : 'bottomLeft';
    return { cardTooltipPlacement, descriptionTooltipPlacement };
  }

  descriptionTooltipPlacement = alignRight ? 'topRight' : 'topLeft';
  return { cardTooltipPlacement, descriptionTooltipPlacement };
};
